import React from 'react'
import {UsePlayerIFrameLogicData, PlayerIframeUrlVars, ViewEvents} from './types'
import {extensionUtils} from 'services/extension'
import {
  initSegmentsPlayerEventBus,
  subscriptionsByLoadDocument,
  Unsubscribe
} from 'common/utils/segmentsPlayerEventBus'
// @ts-ignore
import {sendViewEvent, EVENTS, getISODateNow} from '../../../services/view-events'
import {loadDocumentData} from './api'
import {Segment} from '../SegmentsPlayer/types'
import {PlayerIFrameProps} from './types'
import {getUrlVars} from  'powtoon-commons/utils'
import {createLogger} from 'logging'

const logger = createLogger('segments-player', 'player-iframe')
initSegmentsPlayerEventBus()

const SEGMENTS_FALLBACK_DURATION = 20000
const UPLOAD_POLLING_INTERVAL = 500

const parseStringToBoolean = (str: string): boolean => str.toLowerCase() === 'true'

const isViewEventsEnabled = (urls: PlayerIframeUrlVars): boolean =>
  !!urls.enable_view_events && parseStringToBoolean(urls.enable_view_events)

const getTimeInSeconds = (time: number) => Math.floor(time * 1000) / 1000
export const usePlayerIFrameLogic = (componentProps: PlayerIFrameProps): UsePlayerIFrameLogicData => {
  const urls: PlayerIframeUrlVars = React.useMemo(() => getUrlVars(), [])
  const [thumbUrl, setThumbUrl] = React.useState<string>('')
  const [uploadProgress, setUploadProgress] = React.useState<number|undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [unifiedVideoSegment, setUnifiedVideoUrl] = React.useState<Segment[] | undefined>(undefined)
  const [segments, setSegments] = React.useState<Segment[]>([])
  const segmentsPollingStarted = React.useRef(0)
  const [powtoonLoadStarts, setPowtoonLoadStarts] = React.useState<boolean>(true)
  const [maxPlayTime, setMaxPlayTime] = React.useState<number>(0)
  const innerConsts = React.useMemo(
    () => ({
      handleWindowUnload: () =>
        sendViewEvent({[EVENTS.MAX_VIEW_TIME]: getTimeInSeconds(maxPlayTime)}, {forceSend: true}),
      viewEventsEnabled: isViewEventsEnabled(urls)
    }),
    [maxPlayTime]
  )

  const viewEvents: ViewEvents = React.useMemo(
    () => ({
      onPlay: () => sendViewEvent({[EVENTS.PLAYBACK_STARTED]: getISODateNow()}),
      onPlayerReady: controller => {
        sendViewEvent({[EVENTS.POWTOON_READY]: getISODateNow()})
        componentProps.onPlayerReady(controller)
      },
      onTotalTimeChanged: currentTime => {
        setMaxPlayTime(prev => Math.max(prev, currentTime))
      },
      onEnd: () => {
        sendViewEvent({
          [EVENTS.PLAYBACK_COMPLETED]: getISODateNow(),
          [EVENTS.MAX_VIEW_TIME]: getTimeInSeconds(maxPlayTime)
        })
        componentProps.onEnd()
      }
    }),
    [maxPlayTime]
  )

  const subscriptions = React.useRef<Unsubscribe>()

  const onLoadDocumentData = React.useCallback((options) => {
    loadDocumentData(options).then(
      ({video_url, segments: loadedSegments, isV2, thumb_url}) => {
        setThumbUrl(thumb_url)
        if (video_url) {
          setUnifiedVideoUrl([{video_url, audio_url: video_url}])
          setIsLoading(false)
          subscriptions.current?.unsubscribeAll()
        }
        else if (loadedSegments || isV2) {
          if (segmentsPollingStarted.current === 0) {
            segmentsPollingStarted.current = (Date.now())
          }
          else if (Date.now() - segmentsPollingStarted.current >= SEGMENTS_FALLBACK_DURATION &&
          loadedSegments && !isV2) {
            logger.warn('Using segments player')
            setSegments(loadedSegments)
            setIsLoading(false)
          
          }
        }
      }
    )
  }, [])

  const [uploadPollingInterval, setUploadPollingInterval] = React.useState<number|undefined>(undefined)
  const pollUploadProgress = React.useCallback(async () => {
    if (!urls.powtoon) {
      clearInterval(uploadPollingInterval)
      setUploadPollingInterval(undefined)
      return
    }
    const progress = await extensionUtils.getUploadProgress(urls.powtoon)

    setUploadProgress(progress)
    if (progress === 100 ) {
      setUploadPollingInterval(interval => {
        clearInterval(interval)
        return undefined
      })
    }
  }, [urls, uploadPollingInterval])

  React.useEffect(() => {
    if (isViewEventsEnabled(urls)) {
      setPowtoonLoadStarts(getISODateNow() as any)
      subscriptions.current = subscriptionsByLoadDocument(() => onLoadDocumentData(urls), urls?.powtoon)
      onLoadDocumentData(urls)
    }
  }, [urls])

  React.useEffect(() => {
    if (urls.powtoon) {
      // initial value is important, because if starting with undefined, it means that no active upload is present
      extensionUtils.getUploadProgress(urls.powtoon).then(newProgress => {
        if (newProgress !== undefined) {
          setUploadProgress(oldProgress => {
            if (oldProgress === undefined && newProgress === 100) {
              return undefined
            }
            else {
              const interval  = setInterval(pollUploadProgress, UPLOAD_POLLING_INTERVAL)
              setUploadPollingInterval(Number(interval))
              return newProgress
            }
          })
        }
      })
    }
  }, [urls])

  React.useEffect(
    () => {
      if (isViewEventsEnabled(urls)) {
        window.addEventListener('beforeunload', innerConsts.handleWindowUnload)
      }
      return () => window.removeEventListener('beforeunload', innerConsts.handleWindowUnload)
    },
    [maxPlayTime]
  )

  return {
    isLoading,
    segments,
    autoplay: false,
    hideControls: false,
    missingPowtoonId: !urls.powtoon,
    viewEvents: innerConsts.viewEventsEnabled ? viewEvents : {},
    unifiedVideoSegment,
    uploadProgress,
    thumbUrl
  }
}
